import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '@invent/wl-ui-kit-next/ui';
import { FileWarning } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { StatusChip } from '@/design';
import { useAdminData } from '@/domains/admin';
import { formatDateTime } from '@/domains/survey';
import { overviewApi } from '../api/overview-api';
import type {
  ContentOverviewGap,
  ContentOverviewKind,
  IContentOverview
} from '../api/overview-contracts';
import { CONTENT_PATHS, eventPath, postPath } from '../paths';
import { EmptyNote, actionWord, personName } from '../shared';
import { ContentPage } from './content-page';

/**
 * Where the content team lands (§7.11).
 *
 * **It answers three questions and does not try to be a dashboard.** What is waiting on me, what
 * just changed on the live site, and what is going out incomplete. Those are the questions a
 * marketing team opens a CMS with during a launch week; counts of everything are what gets added
 * when nobody asked what the screen is for.
 *
 * **The metadata panel exists because WEB-15's failure is silent.** A post published without a
 * description looks fine in the editor and wrong in every channel it is pasted into, and nobody
 * finds out until it is already there. Listing the gaps across the whole site is the only place
 * that can be caught before it matters.
 *
 * **All of it is survey-api's, in one read** (`overview-api.ts`): posts, the team page and events.
 * The site pages have no store yet, so they are not on this screen rather than drawn from fixtures.
 */
export function ContentOverviewPage() {
  const { data, error, reload } = useAdminData<IContentOverview>((signal) =>
    overviewApi.read(signal)
  );

  return (
    <ContentPage title="Website" lede="What is waiting, what went out incomplete, and what changed.">
      {error && (
        <Alert variant="destructive">
          <AlertTitle>The overview could not be read</AlertTitle>
          <AlertDescription className="flex flex-wrap items-center gap-3">
            {error}
            <Button size="sm" variant="outline" onClick={() => void reload()}>
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <section className="grid gap-3 sm:grid-cols-3">
        <Stat label="Posts live" value={data?.live.posts} to={CONTENT_PATHS.news} />
        <Stat label="People on the team page" value={data?.live.team} to={CONTENT_PATHS.team} />
        <Stat label="Events listed" value={data?.live.events} to={CONTENT_PATHS.events} />
      </section>

      {data && (
        <>
          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Waiting to go out ({data.waiting.length})</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-2">
                {data.waiting.length === 0 && <EmptyNote>Nothing is sitting in draft.</EmptyNote>}
                {data.waiting.map((row) => (
                  <div
                    key={`${row.kind}-${row.id}`}
                    className="flex flex-wrap items-center gap-2 border-b border-border py-2 text-sm last:border-b-0"
                  >
                    <RecordLink
                      kind={row.kind}
                      id={row.id}
                      className="min-w-0 flex-1 truncate font-medium hover:underline"
                    >
                      {row.title}
                    </RecordLink>
                    <StatusChip tone="neutral">{KIND_WORD[row.kind]}</StatusChip>
                    <span className="text-xs text-muted-foreground">
                      {formatDateTime(row.updatedAt)} · {personName(row.updatedBy)}
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex-row items-center justify-between gap-2">
                <CardTitle className="text-sm">Published without metadata</CardTitle>
                {data.incomplete.length > 0 && (
                  <StatusChip tone="attention">{data.incomplete.length}</StatusChip>
                )}
              </CardHeader>
              <CardContent className="grid gap-2">
                {data.incomplete.length === 0 && (
                  <EmptyNote>
                    Everything live carries a title, a description and a sharing image.
                  </EmptyNote>
                )}
                {data.incomplete.map((row) => (
                  <div
                    key={`${row.kind}-${row.id}`}
                    className="grid gap-0.5 border-b border-border py-2 last:border-b-0"
                  >
                    <RecordLink
                      kind={row.kind}
                      id={row.id}
                      className="truncate text-sm font-medium text-[var(--color-sapphire)] hover:underline"
                    >
                      {row.title}
                    </RecordLink>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <FileWarning className="h-3 w-3" aria-hidden />
                      {KIND_WORD[row.kind]} · no{' '}
                      {row.missing.map((gap) => GAP_WORD[gap]).join(', no ')}
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">What changed</CardTitle>
            </CardHeader>
            <CardContent>
              {data.activity.length === 0 && <EmptyNote>Nothing has been written yet.</EmptyNote>}
              <ol className="grid gap-0">
                {data.activity.map((entry, index) => (
                  <li
                    key={`${entry.kind}-${entry.id}-${entry.at}-${index}`}
                    className="flex flex-wrap items-baseline gap-x-2 border-b border-border py-2 text-sm last:border-b-0"
                  >
                    <span className="text-muted-foreground">{formatDateTime(entry.at)}</span>
                    <span>
                      <strong className="font-medium">{personName(entry.by)}</strong>{' '}
                      {actionWord(entry.action)}{' '}
                      <RecordLink
                        kind={entry.kind}
                        id={entry.id}
                        className="text-[var(--color-royal-blue)] hover:underline"
                      >
                        {entry.title}
                      </RecordLink>
                    </span>
                    {entry.note && (
                      <span className="w-full text-xs text-muted-foreground">{entry.note}</span>
                    )}
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        </>
      )}
    </ContentPage>
  );
}

const KIND_WORD: Record<ContentOverviewKind, string> = {
  post: 'Post',
  member: 'Team',
  event: 'Event'
};

const GAP_WORD: Record<ContentOverviewGap, string> = {
  title: 'page title',
  description: 'description',
  'social-image': 'sharing image'
};

/** A record's editor. A person is edited beside the team list, which has no address per person. */
function RecordLink({
  kind,
  id,
  className,
  children
}: {
  kind: ContentOverviewKind;
  id: string;
  className?: string;
  children: React.ReactNode;
}) {
  const to = kind === 'post' ? postPath(id) : kind === 'event' ? eventPath(id) : CONTENT_PATHS.team;
  return (
    <Link to={to} className={className}>
      {children}
    </Link>
  );
}

function Stat({ label, value, to }: { label: string; value: number | undefined; to: string }) {
  return (
    <Button
      asChild
      variant="outline"
      className="h-auto justify-start whitespace-normal px-4 py-3 text-left"
    >
      <Link to={to}>
        <span className="grid gap-0.5">
          <span className="text-2xl font-light text-[var(--color-royal-blue)] tabular-nums">
            {value ?? '–'}
          </span>
          <span className="text-xs text-muted-foreground">{label}</span>
        </span>
      </Link>
    </Button>
  );
}
